import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'

export default function useSystemMessages() {
  const { user, getToken } = useAuth()
  const [messages, setMessages] = useState([])
  const [unread, setUnread]     = useState(0)

  const fetch = useCallback(async () => {
    if (!user) return
    try {
      const token = await getToken()
      if (!token) return // skip when session not yet populated; next poll will retry
      const headers = { Authorization: `Bearer ${token}` }
      const [msgRes, countRes] = await Promise.all([
        axios.get('/api/system-messages', { headers }),
        axios.get('/api/system-messages/unread-count', { headers }),
      ])
      setMessages(msgRes.data)
      setUnread(countRes.data.unread)
    } catch (err) { console.error('[useSystemMessages] Failed to fetch system messages:', err) }
  }, [user, getToken])

  useEffect(() => {
    fetch()
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetch()
    }, 60000) // poll every 60s; pause while tab is backgrounded
    const onChanged = () => fetch()
    window.addEventListener('system-messages-changed', onChanged)
    return () => { clearInterval(interval); window.removeEventListener('system-messages-changed', onChanged) }
  }, [fetch])

  const markRead = useCallback(async (id) => {
    try {
      const token = await getToken()
      await axios.patch(`/api/system-messages/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } })
      setMessages(prev => prev.map(m => m.id === id ? { ...m, read: true } : m))
      setUnread(prev => Math.max(0, prev - 1))
    } catch (err) { console.error('[useSystemMessages] Failed to mark message as read:', err) }
  }, [getToken])

  const markAllRead = useCallback(async () => {
    try {
      const token = await getToken()
      await axios.patch('/api/system-messages/read-all', {}, { headers: { Authorization: `Bearer ${token}` } })
      setMessages(prev => prev.map(m => ({ ...m, read: true })))
      setUnread(0)
    } catch (err) { console.error('[useSystemMessages] Failed to mark all messages as read:', err) }
  }, [getToken])

  return { messages, unread, markRead, markAllRead, refresh: fetch }
}
